import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, Plus, ChevronLeft, ChevronRight, Loader2, MapPin, IndianRupee, Camera, Paperclip, Trash2, CheckCircle2, XCircle, Clock, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 5;
const STATUS_META = {
  planned:   { label: "Planned",   short: "PLN", tone: "bg-slate-100 text-slate-700 border-slate-200",   icon: Clock },
  completed: { label: "Awaiting review", short: "REV", tone: "bg-amber-100 text-amber-800 border-amber-200", icon: ClipboardCheck },
  approved:  { label: "Approved",  short: "OK",  tone: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  rejected:  { label: "Rejected",  short: "NO",  tone: "bg-rose-100 text-rose-800 border-rose-200",    icon: XCircle },
};
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const isoDate = (d) => d.toISOString().slice(0, 10);
const dayCells = (year, month) => {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export default function Planner() {
  const { user } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [visits, setVisits] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [detailVisit, setDetailVisit] = useState(null);

  const month = monthKey(cursor);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, o] = await Promise.all([
        api.get(`/visits?month=${month}`),
        api.get("/outlets").catch(() => ({ data: [] })),
      ]);
      setVisits(v.data);
      setOutlets(o.data.filter((x) => !x.is_internal));
    } finally { setLoading(false); }
  }, [month, user]);
  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => dayCells(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const byDate = useMemo(() => {
    const m = {};
    for (const v of visits) (m[v.visit_date] = m[v.visit_date] || []).push(v);
    return m;
  }, [visits]);

  const openAdd = (dateIso) => { setAddDate(dateIso); setAddOpen(true); };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-indigo-600" /> Monthly Visit Planner
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Schedule outlet visits, log expenses, and submit reports for admin review.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="planner-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
          <div data-testid="planner-month-label" className="min-w-40 text-center font-semibold text-slate-900">{cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}</div>
          <Button variant="outline" size="icon" data-testid="planner-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-4 shadow-sm">
        <div className="grid grid-cols-7 text-[10px] font-bold text-slate-500 uppercase mb-1">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="px-1.5 py-1">{d}</div>)}
        </div>
        {loading && <div className="py-10 text-center"><Loader2 className="w-5 h-5 mx-auto text-slate-400 animate-spin" /></div>}
        {!loading && (
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, i) => {
              if (!cell) return <div key={i} className="min-h-24 rounded-md bg-slate-50/60" />;
              const iso = isoDate(cell);
              const dayVisits = byDate[iso] || [];
              const isToday = iso === isoDate(new Date());
              return (
                <div
                  key={i}
                  data-testid={`planner-day-${iso}`}
                  className={`min-h-24 rounded-md border p-1.5 ${isToday ? "border-indigo-400 ring-1 ring-indigo-200 bg-indigo-50/30" : "border-slate-100"} hover:bg-slate-50 transition-colors`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold ${isToday ? "text-indigo-700" : "text-slate-700"}`}>{cell.getDate()}</span>
                    <button
                      onClick={() => openAdd(iso)}
                      data-testid={`planner-add-${iso}`}
                      title="Schedule a visit"
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 hover:opacity-100 w-5 h-5 rounded bg-indigo-600 text-white inline-flex items-center justify-center"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayVisits.map((v) => {
                      const meta = STATUS_META[v.status] || STATUS_META.planned;
                      const Icon = meta.icon;
                      return (
                        <button
                          key={v.id}
                          data-testid={`planner-visit-${v.id}`}
                          onClick={() => setDetailVisit(v)}
                          aria-label={`Visit at ${v.outlet_code} on ${v.visit_date} — ${meta.label}`}
                          title={`${v.outlet_code} · ${meta.label}`}
                          className={`w-full text-left rounded px-1.5 py-1 text-[10px] font-semibold border ${meta.tone} flex items-center gap-1 truncate`}
                        >
                          <Icon className="w-2.5 h-2.5 shrink-0" />
                          <span className="truncate">{v.outlet_code}</span>
                          <span className="ml-auto text-[9px] opacity-75 truncate">{meta.short}</span>
                        </button>
                      );
                    })}
                    {dayVisits.length === 0 && (
                      <button
                        onClick={() => openAdd(iso)}
                        data-testid={`planner-day-empty-${iso}`}
                        className="w-full text-[9px] text-slate-400 hover:text-indigo-600 text-left py-0.5"
                      >
                        + add visit
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <AddVisitDialog date={addDate} outlets={outlets} onSaved={(v) => { setAddOpen(false); setVisits((p) => [...p, v]); }} />
      </Dialog>

      <Dialog open={!!detailVisit} onOpenChange={(v) => !v && setDetailVisit(null)}>
        {detailVisit && (
          <VisitDetailDialog
            visit={detailVisit}
            onUpdated={(next) => { setVisits((prev) => prev.map((x) => x.id === next.id ? next : x)); setDetailVisit(next); }}
            onDeleted={(id) => { setVisits((prev) => prev.filter((x) => x.id !== id)); setDetailVisit(null); }}
            onClose={() => setDetailVisit(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

function AddVisitDialog({ date, outlets, onSaved }) {
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmed = location.trim();
    if (!trimmed) { toast.error("Enter a visit location"); return; }
    // If the typed value matches a known outlet (by "CODE · Name" label or by
    // plain code) we send outlet_id so the visit stays linked to the system
    // record. Otherwise it goes through as free-text location.
    const match = outlets.find((o) =>
      `${o.code} · ${o.name}`.toLowerCase() === trimmed.toLowerCase() ||
      o.code.toLowerCase() === trimmed.toLowerCase()
    );
    const payload = match
      ? { outlet_id: match.id, visit_date: date, notes }
      : { location: trimmed, visit_date: date, notes };
    setBusy(true);
    try {
      const { data } = await api.post("/visits", payload);
      toast.success("Visit scheduled");
      onSaved(data);
      setLocation(""); setNotes("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Schedule visit — {date}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div>
          <Label htmlFor="planner-add-location">Visit Location</Label>
          <Input
            id="planner-add-location"
            data-testid="planner-add-location"
            list="planner-outlet-suggestions"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Pick an outlet or type any location (e.g. Main Market, Supplier Office)"
            className="mt-1.5 h-11"
            autoComplete="off"
          />
          <datalist id="planner-outlet-suggestions">
            {outlets.map((o) => (
              <option key={o.id} value={`${o.code} · ${o.name}`} />
            ))}
          </datalist>
          <p className="text-[11px] text-slate-500 mt-1">Start typing to pick from your outlets, or write in a custom destination.</p>
        </div>
        <div>
          <Label>Pre-visit agenda (optional)</Label>
          <Textarea data-testid="planner-add-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" placeholder="Focus areas or checklist for the visit" />
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="planner-add-save" disabled={busy || !location.trim()} onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function VisitDetailDialog({ visit, onUpdated, onDeleted, onClose }) {
  const [remarks, setRemarks] = useState(visit.remarks || "");
  const [expense, setExpense] = useState(visit.expense_amount || 0);
  const [receipt, setReceipt] = useState(null);
  const [receiptId, setReceiptId] = useState(visit.receipt_file_id || null);
  const [photos, setPhotos] = useState(visit.photo_ids || []);
  const [busy, setBusy] = useState(false);
  const readonly = visit.status === "approved";

  const uploadOne = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.id;
  };

  const addPhotos = async (fileList) => {
    if (!fileList.length) return;
    if (photos.length + fileList.length > MAX_PHOTOS) { toast.error(`Max ${MAX_PHOTOS} photos per visit`); return; }
    setBusy(true);
    try {
      const ids = [];
      for (const f of fileList) ids.push(await uploadOne(f));
      setPhotos((prev) => [...prev, ...ids]);
      toast.success(`${ids.length} photo${ids.length > 1 ? "s" : ""} added`);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const attachReceipt = async () => {
    if (!receipt) return;
    setBusy(true);
    try { const id = await uploadOne(receipt); setReceiptId(id); toast.success("Receipt attached"); }
    catch { toast.error("Receipt upload failed"); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    if (!remarks.trim()) { toast.error("Remarks are required"); return; }
    setBusy(true);
    try {
      await api.post(`/visits/${visit.id}/complete`, {
        remarks: remarks.trim(),
        expense_amount: Number(expense) || 0,
        receipt_file_id: receiptId,
        photo_ids: photos,
      });
      const { data: fresh } = await api.get(`/visits/${visit.id}`);
      toast.success("Visit submitted for admin review");
      onUpdated(fresh);
      if (onClose) onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
    finally { setBusy(false); }
  };

  const removePlanned = async () => {
    if (!confirm("Delete this planned visit?")) return;
    try { await api.delete(`/visits/${visit.id}`); onDeleted(visit.id); toast.success("Visit removed"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const meta = STATUS_META[visit.status] || STATUS_META.planned;

  return (
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-indigo-600" /> {visit.outlet_code} · {visit.visit_date}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.tone}`}>
          <meta.icon className="w-3 h-3" /> {meta.label}
        </div>
        {visit.notes && <div className="text-xs text-slate-600 border-l-2 border-slate-200 pl-3">Agenda: {visit.notes}</div>}
        {visit.decision_note && (
          <div className={`text-xs rounded p-2 ${visit.status === "approved" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            Admin note: {visit.decision_note}
          </div>
        )}

        <div>
          <Label>Visit remarks</Label>
          <Textarea data-testid="planner-remarks" disabled={readonly} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="What did you inspect? Findings, follow-ups…" className="mt-1.5 min-h-24" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Expense (₹)</Label>
            <div className="relative mt-1.5">
              <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="planner-expense" type="number" min="0" step="0.01" disabled={readonly} value={expense} onChange={(e) => setExpense(e.target.value)} className="pl-7" />
            </div>
          </div>
          <div>
            <Label>Receipt</Label>
            {receiptId ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${receiptId}`} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:underline bg-indigo-50 border border-indigo-100 rounded px-2 py-1.5">
                <Paperclip className="w-3.5 h-3.5" /> View receipt
              </a>
            ) : (
              <div className="flex items-center gap-1.5 mt-1.5">
                <input data-testid="planner-receipt" type="file" accept="application/pdf,image/*" disabled={readonly} onChange={(e) => setReceipt(e.target.files?.[0] || null)} className="text-[10px] max-w-32" />
                {receipt && <Button size="sm" variant="outline" onClick={attachReceipt} disabled={busy}>Attach</Button>}
              </div>
            )}
          </div>
        </div>

        <div>
          <Label>Field photos ({photos.length}/{MAX_PHOTOS})</Label>
          {!readonly && photos.length < MAX_PHOTOS && (
            <input
              type="file"
              accept="image/*"
              multiple
              data-testid="planner-photos"
              disabled={busy}
              onChange={(e) => addPhotos(Array.from(e.target.files || []))}
              className="mt-1.5 block text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
          )}
          {photos.length > 0 && (
            <div className="mt-2 grid grid-cols-3 gap-2">
              {photos.map((pid) => (
                <div key={pid} className="relative rounded-md overflow-hidden border border-slate-200 aspect-square">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}`} alt="Visit" className="w-full h-full object-cover" />
                  {!readonly && (
                    <button onClick={() => setPhotos((p) => p.filter((x) => x !== pid))} className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded p-0.5 opacity-90 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
        {visit.status === "planned" && (
          <Button variant="outline" onClick={removePlanned} className="text-rose-700 border-rose-200 hover:bg-rose-50">Delete plan</Button>
        )}
        {!readonly && (
          <Button data-testid="planner-submit" onClick={complete} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (visit.status === "completed" ? "Re-submit" : "Submit report")}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
